/**
 * EvidenceLeadSection — the readiness-profile mockup, positioned directly
 * above EvidenceSection (moved out of the hero per the redesign — the
 * audience research found this segment already feels competent and needs
 * the *problem* named first, not a product screenshot). Illustrative only:
 * no live diagnostic result is wired up here.
 */

import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface Bar {
  labelKey: string;
  pct: number;
  tone: "ok" | "warn" | "neutral";
}

const BARS: Bar[] = [
  { labelKey: "EvidenceLeadSection.coreMlLabel", pct: 82, tone: "ok" },
  { labelKey: "EvidenceLeadSection.systemsLabel", pct: 58, tone: "neutral" },
  { labelKey: "EvidenceLeadSection.evidenceLabel", pct: 34, tone: "warn" },
  { labelKey: "EvidenceLeadSection.positioningLabel", pct: 41, tone: "warn" },
];

const STAGE_KEYS = [
  "EvidenceLeadSection.stageTarget",
  "EvidenceLeadSection.stageDiagnose",
  "EvidenceLeadSection.stageBuild",
  "EvidenceLeadSection.stageProve",
  "EvidenceLeadSection.stagePosition",
  "EvidenceLeadSection.stageDefend",
];

export default function EvidenceLeadSection() {
  const { t } = useTranslation("home");
  return (
    <Box component="section" sx={{ textAlign: "center" }}>
      <Typography component="h3" sx={{ fontSize: 24, fontWeight: 500, mb: 3 }}>
        {t("EvidenceLeadSection.heading")}
      </Typography>

      <Box
        sx={{
          maxWidth: 480,
          mx: "auto",
          borderRadius: "14px",
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          textAlign: "left",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Typography
            sx={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 12, color: "text.secondary" }}
          >
            {t("EvidenceLeadSection.panelTitle")}
          </Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography sx={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 12, color: "text.secondary" }}>
                {t("EvidenceLeadSection.readinessLabel")}
              </Typography>
              <Typography sx={{ fontSize: 32, fontWeight: 500 }}>
                <Box component="span" sx={{ color: "secondary.main" }}>64</Box> / 100
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1}>
            {BARS.map((b) => (
              <Stack key={b.labelKey} direction="row" alignItems="center" spacing={1.5}>
                <Typography sx={{ fontSize: 13, color: "text.secondary", width: 130, flexShrink: 0 }}>
                  {t(b.labelKey)}
                </Typography>
                <Box sx={{ flex: 1, height: 6, borderRadius: "100px", bgcolor: "divider", overflow: "hidden" }}>
                  <Box
                    sx={{
                      width: `${b.pct}%`,
                      height: "100%",
                      borderRadius: "100px",
                      bgcolor:
                        b.tone === "ok" ? "success.main" : b.tone === "warn" ? "secondary.main" : "identity.main",
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary", fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
                  {b.pct}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ mt: 2.5, pt: 2, borderTop: "1px dashed", borderColor: "divider" }}>
            {STAGE_KEYS.map((k, i) => (
              <Typography
                key={k}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                  fontSize: 10,
                  color: i <= 1 ? "success.main" : i === 2 ? "secondary.main" : "text.secondary",
                }}
              >
                {t(k)}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Box>

      <Typography sx={{ mt: 2, fontSize: 13.5, color: "text.secondary", maxWidth: 420, mx: "auto" }}>
        {t("EvidenceLeadSection.caption")}
      </Typography>
    </Box>
  );
}
